import { memo, useRef, useEffect } from "react";
import { useDrop } from "react-dnd";
import { V2Table } from "./V2Table";
import { RESERVATION_DRAG_TYPE, ReservationDragItem } from "./FloorPlanDndProvider";
import { V2Position } from "@/hooks/useFloorPlanV2";
import { Table, Reservation } from "@/types/table";
import { TableStatus } from "@/lib/floorPlanUtils";

interface Props {
  position: V2Position;
  table: Table;
  zoneColor?: string;
  interactionMode?: 'edit' | 'service' | 'view';
  status?: TableStatus;
  reservation?: Reservation;
  dayReservations?: Reservation[];
  isSelected: boolean;
  hasOverlap: boolean;
  isHighlighted?: boolean;
  compact?: boolean;
  /** Drop is only wired in service mode. */
  canAcceptDrops: boolean;
  /** Returns drop styling state for this table given the in-flight item + hover. */
  resolveDropState: (tableId: string, item: ReservationDragItem | null, hovered: boolean) =>
    | 'valid' | 'invalid' | 'hover-valid' | 'hover-invalid' | 'swap' | 'hover-swap' | null;
  /** Called when user releases a drop on this table. */
  onReservationDrop: (reservationId: string, tableId: string) => void;
  /** Returns true if the drop passes hard constraints (physical overlap, locked, …). */
  isHardAllowed: (tableId: string, item: ReservationDragItem) => boolean;
}

/**
 * Thin wrapper that adds react-dnd useDrop around V2Table.
 * The drop ref is attached to V2Table's outer div so the bounding rect
 * matches the rendered table — necessary for accurate hit-testing.
 */
export const V2TableDroppable = memo((props: Props) => {
  const {
    canAcceptDrops, resolveDropState, onReservationDrop, isHardAllowed,
    position, ...rest
  } = props;
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [{ isOver, item }, dropRef] = useDrop<
    ReservationDragItem,
    void,
    { isOver: boolean; item: ReservationDragItem | null }
  >(() => ({
    accept: RESERVATION_DRAG_TYPE,
    canDrop: (it) => canAcceptDrops && isHardAllowed(position.tableId, it),
    drop: (it) => {
      onReservationDrop(it.reservationId, position.tableId);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      item: monitor.getItem(),
    }),
  }), [canAcceptDrops, position.tableId, isHardAllowed, onReservationDrop]);

  // Connect dropRef to the table's actual DOM node
  useEffect(() => {
    dropRef(tableRef.current);
  }, [dropRef]);

  const dropState = canAcceptDrops
    ? resolveDropState(position.tableId, item, isOver)
    : null;

  return (
    <V2Table
      ref={tableRef}
      position={position}
      {...rest}
      dropState={dropState}
    />
  );
});
V2TableDroppable.displayName = 'V2TableDroppable';
